// Third-party
import { v4 as uuidv4 } from "uuid";
import { useQuery } from "@tanstack/react-query";

// MUI
import { Box, Dialog, DialogContent, DialogTitle, Skeleton, Stack, Typography } from "@mui/material";

// API
import { getVehicleDetails } from "@DataPages/api/inventory/queries";

// Components
import vehicleColumnDetail from "@DataPages/components/Inventory/Widgets/VehicleColumnDetail";
import DialogNavigation from "@DataPages/components/Inventory/Layout/DialogNavigation";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";

// External Components
import Masonry from "react-masonry-css";

// Icons
import CloseIcon from "@mui/icons-material/Close";

// Types
import { Inventory } from "@/DataPages/types/inventory/models/inventory";

type VehicleDetailedInformationDialogProps = {
	locationId: string;
	rows: Inventory[];
	isOpen: boolean;
	onClose: () => void;
	selectedVehicle: Inventory;
	onNavigate: (direction: "prev" | "next") => void;
	disabledButtons: { prev: boolean; next: boolean };
};

const randomHeightArray = [
	{ _id: 1, height: 70 },
	{ _id: 2, height: 200 },
	{ _id: 3, height: 200 },
	{ _id: 4, height: 300 },
	{ _id: 5, height: 250 },
	{ _id: 6, height: 200 },
	{ _id: 7, height: 150 },
];

export default function VehicleDetailsDialog(props: Readonly<VehicleDetailedInformationDialogProps>) {
	const { data, isLoading } = useQuery({
		queryKey: ["vehicleDetails", props.selectedVehicle?._id],
		queryFn: () => getVehicleDetails(props.locationId, props.selectedVehicle?._id) as Promise<Inventory>,
		enabled: Boolean(props.locationId) && Boolean(props.isOpen) && Boolean(props.selectedVehicle?._id),
	});

	return (
		<Dialog
			open={props.isOpen}
			onClose={props.onClose}
			maxWidth="md"
			scroll="paper"
			aria-labelledby="masonry-dialog-title"
			aria-describedby="masonry-dialog-description"
			classes={{
				paper: "!rounded-[10px] !pt-[2px]",
			}}
		>
			<DialogTitle
				classes={{
					root: "!ml-2",
				}}
			>
				<Stack direction={"row"} justifyContent={"space-between"} pb={"0px"}>
					<Typography className={"!font-montserrat !text-[18px] !text-[#666666]"}>
						{`${props.selectedVehicle?.year} ${props.selectedVehicle?.make} ${props.selectedVehicle?.model} \
						${(props.selectedVehicle?.trim as string) ?? ""} - Stock# ${props.selectedVehicle?.stockId}`}
					</Typography>
					<SimpleIconButton
						className={"!absolute top-2 right-2"}
						aria-label="close"
						onClick={props.onClose}
						icon={<CloseIcon className=" !text-[#B3B3B3]" />}
					/>
				</Stack>
			</DialogTitle>

			<DialogContent>
				<Stack alignItems={"center"}>
					<Masonry breakpointCols={3} className="my-masonry-grid" columnClassName="my-masonry-grid_column">
						{isLoading
							? randomHeightArray.map((item, idx) => (
									<Box key={item._id} width={250}>
										<Skeleton variant={"text"} width={120} className={"rounded-md !mb-3"} />
										<Skeleton
											variant={"rectangular"}
											width={250}
											height={randomHeightArray[idx].height}
											className={"rounded-sm"}
										/>
									</Box>
							  ))
							: vehicleColumnDetail({ data, inventoryId: props.selectedVehicle?._id })?.map((item) => {
									return <Box key={uuidv4()}>{item}</Box>;
							  })}
					</Masonry>

					<DialogNavigation
						className="bg-white "
						onPrev={() => props.onNavigate("prev")}
						onNext={() => props.onNavigate("next")}
						disabledButtons={props.disabledButtons}
					/>
				</Stack>
			</DialogContent>
		</Dialog>
	);
}
